import { ColumnDef } from "@tanstack/react-table"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"

import { handleToast } from "utils/messages"
import { history } from "utils/routes"

import { getPlants } from "../../../companies/service/api"
import { Company } from "../../../companies/types"
import { compositionGetByCompanyId } from "../../../compositions/service/api"
import { CompositionType } from "../../../compositions/constants"
import { Composition } from "../../../compositions/types"
import { filterCompanyCompositions } from "../../../genericCompositions/utils"
import { MaterialGroup } from "../../../materials/service/api"
import {
  deleteSubmission,
  postSubmission,
  putSubmission,
} from "../../service/api"
import { SubmissionType } from "../../types"
import SubmissionGrid, { GridRow } from "./SubmissionGrid"
import { useGridSubmit } from "./useGridSubmit"

// Row model — same shape as recycling output, but written as SORTING / not input.
type SorterOutputRow = {
  id?: number
  companyId?: number
  materialsGroupId?: number
  collectionDate?: Date
  compositionId?: number // Application (optional)
  sortedOutputEU?: number
  sortedOutputNonEU?: number
  administrationNumber?: string
}

type LocalRow = GridRow<SorterOutputRow>

const makeKey = () =>
  `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const SorterOutputGrid: React.FC = () => {
  const { t } = useTranslation()

  const [plants, setPlants] = useState<Company[]>([])
  const plantsRef = useRef<Company[]>([])
  const [compositionsByCompany, setCompositionsByCompany] = useState<
    Record<number, Composition[]>
  >({})
  const compositionsByCompanyRef = useRef<Record<number, Composition[]>>({})
  const [polymersByCompany, setPolymersByCompany] = useState<
    Record<number, MaterialGroup[]>
  >({})

  const [rows, setRows] = useState<LocalRow[]>([{ key: makeKey(), row: {} }])

  // Sorter output: sold to recyclers, doesn't count as recycling.
  const buildPayload = useCallback(
    (row: SorterOutputRow) => {
      const netOutputEU = row.sortedOutputEU ?? 0
      const netOutputNonEU = row.sortedOutputNonEU ?? 0
      const total = netOutputEU + netOutputNonEU
      return {
        companyId: row.companyId!,
        materialsGroupId: row.materialsGroupId!,
        compositionId: row.compositionId ?? null,
        collectionDate: row.collectionDate!,
        volumeGross: total,
        volumeNet: total,
        netOutputEU,
        netOutputNonEU,
        grossOutputEU: netOutputEU,
        grossOutputNonEU: netOutputNonEU,
        virginAdditives: 0,
        isInput: false,
        administrationNumber: row.administrationNumber ?? "",
        type: SubmissionType.SORTING,
      }
    },
    [],
  )

  const { states, isSubmitting, submitAll, clearRowState } =
    useGridSubmit<SorterOutputRow>({
      validate: (row) => {
        if (
          row.companyId == null ||
          row.materialsGroupId == null ||
          row.collectionDate == null ||
          (row.sortedOutputEU == null && row.sortedOutputNonEU == null)
        ) {
          return null
        }
        const errors: string[] = []
        if ((row.sortedOutputEU ?? 0) < 0)
          errors.push(t("Output EU must be ≥ 0"))
        if ((row.sortedOutputNonEU ?? 0) < 0)
          errors.push(t("Output Non-EU must be ≥ 0"))
        if (
          (row.sortedOutputEU ?? 0) === 0 &&
          (row.sortedOutputNonEU ?? 0) === 0
        )
          errors.push(t("At least one of EU / Non-EU outputs must be > 0"))
        return errors
      },
      create: async (row) => {
        const { data, errors } = await postSubmission(buildPayload(row) as any)
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data!.id! }
      },
      update: async (row) => {
        const { data, errors } = await putSubmission({
          ...buildPayload(row),
          id: row.id!,
        } as any)
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data?.id ?? row.id! }
      },
    })

  const ensureCompanyData = useCallback(
    (companyId: number) => {
      setPolymersByCompany((prev) => {
        if (prev[companyId]) return prev
        const plant = plantsRef.current.find((p) => p.id === companyId)
        const polymers = plant?.polymers as MaterialGroup[] | undefined
        if (!polymers) return prev
        if (polymers.length === 1) {
          setRows((r) =>
            r.map((row) =>
              row.row.companyId === companyId && row.row.materialsGroupId == null
                ? { ...row, row: { ...row.row, materialsGroupId: polymers[0].id } }
                : row,
            ),
          )
        }
        return { ...prev, [companyId]: polymers }
      })
      setCompositionsByCompany((prev) => {
        if (prev[companyId]) return prev
        compositionGetByCompanyId({
          companyId,
          type: CompositionType.SORTING_OUTPUT,
        } as any).then(({ data }) => {
          if (data) {
            const compositions = data as Composition[]
            compositionsByCompanyRef.current = { ...compositionsByCompanyRef.current, [companyId]: compositions }
            setCompositionsByCompany((p) => ({ ...p, [companyId]: compositions }))
            const filtered = filterCompanyCompositions<Composition>(compositions, undefined, true)
            if (filtered.length === 1) {
              setRows((r) =>
                r.map((row) =>
                  row.row.companyId === companyId && row.row.compositionId == null
                    ? { ...row, row: { ...row.row, compositionId: filtered[0].id } }
                    : row,
                ),
              )
            }
          }
        })
        return prev
      })
    },
    [],
  )

  useEffect(() => {
    getPlants().then((d) => {
      if (!d.data) return
      const loaded = d.data as Company[]
      plantsRef.current = loaded
      setPlants(loaded)
      if (loaded.length !== 1) return
      const companyId = loaded[0].id
      const polymers = loaded[0].polymers as MaterialGroup[] | undefined
      const materialsGroupId = polymers?.length === 1 ? polymers[0].id : undefined
      setPolymersByCompany({ [companyId]: polymers ?? [] })
      setRows((prev) =>
        prev.map((r) =>
          r.row.companyId == null
            ? { ...r, row: { ...r.row, companyId, ...(materialsGroupId != null && { materialsGroupId }) } }
            : r,
        ),
      )
      compositionGetByCompanyId({
        companyId,
        type: CompositionType.SORTING_OUTPUT,
      } as any).then(({ data }) => {
        if (!data) return
        const compositions = data as Composition[]
        compositionsByCompanyRef.current = { [companyId]: compositions }
        setCompositionsByCompany({ [companyId]: compositions })
        const filtered = filterCompanyCompositions<Composition>(compositions, undefined, true)
        if (filtered.length === 1) {
          setRows((prev) =>
            prev.map((r) =>
              r.row.companyId === companyId && r.row.compositionId == null
                ? { ...r, row: { ...r.row, compositionId: filtered[0].id } }
                : r,
            ),
          )
        }
      })
    })
  }, [])

  const updateRow = useCallback(
    (key: string, patch: Partial<SorterOutputRow>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, row: { ...r.row, ...patch } } : r,
        ),
      )
    },
    [],
  )

  const handleAdd = useCallback(() => {
    const single = plantsRef.current.length === 1 ? plantsRef.current[0] : null
    const companyId = single?.id
    const polymers = single?.polymers as MaterialGroup[] | undefined
    const materialsGroupId = polymers?.length === 1 ? polymers[0].id : undefined
    const compositionId = companyId != null
      ? (() => {
          const list = filterCompanyCompositions<Composition>(
            compositionsByCompanyRef.current[companyId] ?? [],
            undefined,
            true,
          )
          return list.length === 1 ? list[0].id : undefined
        })()
      : undefined
    setRows((prev) => [
      ...prev,
      {
        key: makeKey(),
        row: {
          ...(companyId != null && { companyId }),
          ...(materialsGroupId != null && { materialsGroupId }),
          ...(compositionId != null && { compositionId }),
        },
      },
    ])
  }, [])

  const handleRemove = useCallback(
    async (key: string) => {
      const target = rows.find((r) => r.key === key)
      clearRowState(key)
      if (target?.row.id != null) {
        const { errors } = await deleteSubmission({ id: target.row.id })
        if (errors) {
          handleToast(errors, "error")
          return
        }
      }
      setRows((prev) => prev.filter((r) => r.key !== key))
    },
    [clearRowState, rows],
  )

  const handleSubmit = useCallback(async () => {
    const { ok, savedCount, updated } = await submitAll(
      rows.map(({ key, row }) => ({ key, row })),
    )
    if (updated.length > 0) {
      setRows((prev) =>
        prev.map((r) => {
          const u = updated.find((x) => x.key === r.key)
          return u ? { ...r, row: { ...r.row, id: u.serverId } } : r
        }),
      )
    }
    if (ok && savedCount === 0) {
      handleToast(
        [t("Nothing to save — fill in the required fields first.") as string],
        "error",
      )
      return
    }
    if (ok) {
      handleToast([t("Submissions saved") as string])
      history.push("/submissions", { tab: "sortingInput" })
    } else {
      handleToast(
        [
          t("Some rows could not be saved. Check the highlighted rows.") as string,
        ],
        "error",
      )
    }
  }, [rows, submitAll, t])

  const soldTooltip = t("Sold to recyclers")

  const columns = useMemo<ColumnDef<LocalRow, any>[]>(
    () => [
      {
        id: "company",
        header: () => <span>{t("Plant name")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <select
              value={r.companyId ?? ""}
              onChange={(e) => {
                const companyId = e.target.value
                  ? Number(e.target.value)
                  : undefined
                if (companyId) ensureCompanyData(companyId)
                updateRow(row.original.key, {
                  companyId,
                  materialsGroupId: undefined,
                  compositionId: undefined,
                })
              }}
            >
              <option value="">{t("Select…")}</option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 180,
      },
      {
        id: "polymer",
        header: () => <span>{t("Polymer")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const list = r.companyId ? polymersByCompany[r.companyId] ?? [] : []
          return (
            <select
              value={r.materialsGroupId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  materialsGroupId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 140,
      },
      {
        id: "date",
        header: () => <span>{t("Date")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <DatePicker
              selected={r.collectionDate}
              dateFormat="MMM-yy"
              showMonthYearPicker
              showPopperArrow={false}
              onChange={(date: Date) =>
                updateRow(row.original.key, { collectionDate: date })
              }
              className="form-control"
              placeholderText={t("Pick month")}
            />
          )
        },
        size: 130,
      },
      {
        id: "application",
        header: () => <span>{t("Application")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const list = r.companyId
            ? filterCompanyCompositions<Composition>(
                compositionsByCompany[r.companyId] ?? [],
                undefined,
                true,
              )
            : []
          return (
            <select
              value={r.compositionId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  compositionId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.productName}
                </option>
              ))}
            </select>
          )
        },
        size: 180,
      },
      {
        id: "sortedOutputEU",
        header: () => (
          <span>
            {t("Sorted/shredded Output (EU+UK)")}
            <span className="srs-grid-label-info" title={soldTooltip}>
              (i)
            </span>
          </span>
        ),
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.sortedOutputEU ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  sortedOutputEU: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 180,
      },
      {
        id: "sortedOutputNonEU",
        header: () => (
          <span>
            {t("Sorted/shredded Output (Non-EU)")}
            <span className="srs-grid-label-info" title={soldTooltip}>
              (i)
            </span>
          </span>
        ),
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.sortedOutputNonEU ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  sortedOutputNonEU: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 180,
      },
      {
        id: "extra",
        header: () => <span>{t("Extra information")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="text"
              placeholder={t("Free text")}
              value={r.administrationNumber ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  administrationNumber: e.target.value,
                })
              }
            />
          )
        },
        size: 180,
      },
    ],
    [
      compositionsByCompany,
      ensureCompanyData,
      plants,
      polymersByCompany,
      soldTooltip,
      t,
      updateRow,
    ],
  )

  return (
    <SubmissionGrid<SorterOutputRow>
      rows={rows}
      columns={columns}
      states={states}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  )
}

export default SorterOutputGrid
